import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";

import {
    ROUTE_GET_EARN_ACTIVITY,
    ROUTE_GET_EARN_QUEUE,
    ROUTE_GET_EARN_STRATEGY_POSITIONS,
    ROUTE_GET_EARN_VAULTS,
    ROUTE_GET_EARN_VAULT_ALLOCATIONS,
    ROUTE_GET_EARN_VAULT_BY_ADDRESS,
    ROUTE_GET_EARN_VAULT_HISTORY
} from "../constants";
import { EarnVaultSnapshotApi, EarnVaultWithLatestSnapshotApi, FetchEarnVaultSnapshotsParams } from "../types/api/earn";
import { EarnActivityRequestApi, EarnActivityResponseApi } from "../types/api/earn-activity";
import { EarnQueueRequestApi, EarnQueueResponseApi } from "../types/api/earn-queue";
import {
    EarnActivityResponse,
    EarnQueueResponse,
    EarnStrategyPositionsResponse,
    EarnVaultAllocationsResponse,
    EarnVaultRecord,
    EarnVaultSnapshot
} from "../types";
import {
    EarnStrategyPositionsRequestApi,
    EarnStrategyPositionsResponseApi
} from "../types/api/earn-strategy-positions";
import { EarnVaultAllocationsResponseApi, FetchEarnVaultAllocationsParams } from "../types/api/earn-allocations";
import {
    parseEarnVaultFromApi,
    parseEarnVaultListFromApi,
    parseEarnVaultSnapshotListFromApi
} from "../types/parsers/earn-parser";
import { parseEarnActivityResponseFromApi } from "../types/parsers/earn-activity-parser";
import { parseEarnQueueResponseFromApi } from "../types/parsers/earn-queue-parser";
import { parseEarnStrategyPositionsResponseFromApi } from "../types/parsers/earn-strategy-positions-parser";
import { parseEarnVaultAllocationsResponseFromApi } from "../types/parsers/earn-allocations-parser";
import { abfSerializeQueryArgs, marketsUnauthenticatedBaseQuery } from "./util";

const EARN_PUBLIC_VAULT_TAG = "EarnVault";

export const earnVaultsPublicApi = createApi({
    reducerPath: "abfEarnVaultPublicApi",
    baseQuery: marketsUnauthenticatedBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,
    tagTypes: [EARN_PUBLIC_VAULT_TAG],
    endpoints: (builder) => ({
        earnVaults: builder.query<EarnVaultRecord[], void>({
            providesTags: [EARN_PUBLIC_VAULT_TAG],
            query() {
                return {
                    url: ROUTE_GET_EARN_VAULTS,
                    method: "GET"
                };
            },
            transformResponse: (response: EarnVaultWithLatestSnapshotApi[]): EarnVaultRecord[] =>
                parseEarnVaultListFromApi(response)
        }),
        earnVault: builder.query<EarnVaultRecord, string>({
            providesTags: [EARN_PUBLIC_VAULT_TAG],
            query(address) {
                return {
                    url: `${ROUTE_GET_EARN_VAULT_BY_ADDRESS}/${encodeURIComponent(address)}`,
                    method: "GET"
                };
            },
            transformResponse: (response: EarnVaultWithLatestSnapshotApi): EarnVaultRecord =>
                parseEarnVaultFromApi(response)
        }),
        earnQueue: builder.query<EarnQueueResponse, EarnQueueRequestApi>({
            providesTags: [EARN_PUBLIC_VAULT_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_EARN_QUEUE,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: EarnQueueResponseApi): EarnQueueResponse =>
                parseEarnQueueResponseFromApi(response)
        }),
        earnActivity: builder.query<EarnActivityResponse, EarnActivityRequestApi>({
            providesTags: [EARN_PUBLIC_VAULT_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_EARN_ACTIVITY,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: EarnActivityResponseApi): EarnActivityResponse =>
                parseEarnActivityResponseFromApi(response)
        }),
        earnStrategyPositions: builder.query<EarnStrategyPositionsResponse, EarnStrategyPositionsRequestApi>({
            providesTags: [EARN_PUBLIC_VAULT_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_EARN_STRATEGY_POSITIONS,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: EarnStrategyPositionsResponseApi): EarnStrategyPositionsResponse =>
                parseEarnStrategyPositionsResponseFromApi(response)
        }),
        earnVaultHistory: builder.query<EarnVaultSnapshot[], FetchEarnVaultSnapshotsParams>({
            providesTags: [EARN_PUBLIC_VAULT_TAG],
            query(params) {
                return {
                    url: ROUTE_GET_EARN_VAULT_HISTORY,
                    method: "POST",
                    body: params
                };
            },
            transformResponse: (response: EarnVaultSnapshotApi[]): EarnVaultSnapshot[] =>
                parseEarnVaultSnapshotListFromApi(response)
        }),
        earnVaultAllocations: builder.query<EarnVaultAllocationsResponse, FetchEarnVaultAllocationsParams>({
            providesTags: [EARN_PUBLIC_VAULT_TAG],
            query({ address }) {
                return {
                    url: `${ROUTE_GET_EARN_VAULT_ALLOCATIONS}/${encodeURIComponent(address)}/positions`,
                    method: "GET"
                };
            },
            transformResponse: (response: EarnVaultAllocationsResponseApi): EarnVaultAllocationsResponse =>
                parseEarnVaultAllocationsResponseFromApi(response)
        })
    })
});

export const {
    useEarnVaultsQuery,
    useEarnVaultQuery,
    useEarnQueueQuery,
    useEarnActivityQuery,
    useEarnStrategyPositionsQuery,
    useEarnVaultHistoryQuery,
    useEarnVaultAllocationsQuery
} = earnVaultsPublicApi;

export function useEarnVaultsPublicApi() {
    const dispatch = useDispatch();

    return {
        resetEarnVaultPublicApi: () => dispatch(earnVaultsPublicApi.util.invalidateTags([EARN_PUBLIC_VAULT_TAG]))
    };
}
